
import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, ArrowLeft, Loader2, MonitorPlay, MapPin, Locate, UploadCloud, Trash2, ArrowUp, ArrowDown, Check, Box, Gift, RefreshCw, Ban, PauseCircle, Construction, Flag, Maximize, Minimize } from 'lucide-react';
import { Survey, SurveyQuestionDraft, LootItem, LocalizedText } from '../types';
import { submitKioskResponse, uploadSurveyFile } from '../services/surveyService';
import { validateSurveyResponse } from '../services/geminiService';
import KioskContainer from '../components/KioskContainer';
import Logo from '../components/Logo';

// Returns true only when the value is a real, non-empty answer. Empty arrays
// (CHECKBOX/RANKING) and empty matrix objects must count as unanswered.
const isAnswered = (q: SurveyQuestionDraft, val: any): boolean => {
    if (q.question_type === 'MATRIX') return !!val && typeof val === 'object' && Object.keys(val).length > 0;
    if (Array.isArray(val)) return val.length > 0;
    return val !== undefined && val !== null && val !== '';
};

// Helpers for Localization Priority
// Goal: If a non-English language exists (e.g. Khmer), show it first. English becomes secondary.
const getPrimaryText = (obj: LocalizedText): string => {
    const keys = Object.keys(obj);
    if (keys.length === 0) return 'Untitled';
    // Find the first key that IS NOT 'en'
    const nonEnKey = keys.find(k => k !== 'en');
    // If found, return it. Otherwise return 'en' (or the first available).
    const val = nonEnKey ? obj[nonEnKey] : (obj.en || Object.values(obj)[0]);
    return val || 'Untitled';
};

const getSecondaryText = (obj: LocalizedText) => {
    const keys = Object.keys(obj);
    const nonEnKey = keys.find(k => k !== 'en');
    // If we are showing a foreign language as primary, and we have English, show English as secondary.
    if (nonEnKey && obj.en) return obj.en;
    return null;
};

interface KioskProps {
    survey: Survey;
    questions: SurveyQuestionDraft[];
    accessCode?: string;
    onReset: () => void;
}

const SurveyResponseKioskView: React.FC<KioskProps> = ({ survey, questions, accessCode = '' }) => {
  // Kiosk specific state
  const [viewState, setViewState] = useState<'INTRO' | 'RUNNER' | 'SUCCESS'>('INTRO');
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [historyStack, setHistoryStack] = useState<number[]>([]); 
  
  // Validation
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Kiosk Reward & Reset
  const [autoResetTimer, setAutoResetTimer] = useState(15);
  const [wonItem, setWonItem] = useState<LootItem | null>(null);
  const [isUnboxing, setIsUnboxing] = useState(false); 
  const [boxOpen, setBoxOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Auto-Reset Logic
  useEffect(() => {
    let interval: any;
    if (viewState === 'SUCCESS') {
        // Only count down if box is already opened OR if no item was won
        if (!wonItem || boxOpen) {
             interval = setInterval(() => {
                setAutoResetTimer((prev) => {
                  if (prev <= 1) {
                    handleReset();
                    return 15;
                  }
                  return prev - 1;
                });
              }, 1000);
        }
    }
    return () => clearInterval(interval);
  }, [viewState, wonItem, boxOpen]);

  // Auto-Open Box Logic (New Requirement)
  useEffect(() => {
      if (viewState === 'SUCCESS' && wonItem && !boxOpen && !isUnboxing) {
          console.log("Auto-opening mystery box...");
          const timer = setTimeout(() => {
              handleOpenBox();
          }, 500); // Slight delay for visual transition
          return () => clearTimeout(timer);
      }
  }, [viewState, wonItem]);

  const handleReset = () => {
      setAnswers({});
      setCurrentQIndex(0);
      setHistoryStack([]);
      setWonItem(null);
      setBoxOpen(false);
      setIsUnboxing(false);
      setViewState('INTRO');
      setAutoResetTimer(15);
  };

  const startSurvey = () => {
      setViewState('RUNNER');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(e => console.log(e));
        setIsFullscreen(true);
    } else {
        document.exitFullscreen().catch(e => console.log(e));
        setIsFullscreen(false);
    }
  };

  const handleAnswerChange = (value: any) => {
    const currentQ = questions[currentQIndex];
    setAnswers(prev => ({ ...prev, [currentQ.id]: value }));
    setValidationError(null);

    // Kiosk Auto-Advance for simple types
    if (['MULTIPLE_CHOICE', 'RATING'].includes(currentQ.question_type)) {
        setTimeout(() => nextQuestion(), 400); 
    }
  };

  const nextQuestion = async () => {
    const currentQ = questions[currentQIndex];
    const answer = answers[currentQ.id];

    // AI Validation
    if (currentQ.gemini_validation_prompt && typeof answer === 'string' && answer.length > 5) {
        setIsValidating(true);
        try {
            const result = await validateSurveyResponse(
                getPrimaryText(currentQ.question_text), 
                answer, 
                currentQ.gemini_validation_prompt
            );
            if (!result.isValid) {
                setValidationError(result.feedback || "Please provide a more detailed answer.");
                setIsValidating(false);
                return;
            }
        } finally {
            setIsValidating(false);
        }
    }

    setTimeout(() => {
        setHistoryStack(prev => [...prev, currentQIndex]);
        let nextIndex = currentQIndex + 1;

        // Skip Logic. A skip target is matched by order_index; if it can't be
        // resolved (-1) we fall back to the next sequential question instead of
        // ending the survey early.
        if (currentQ.question_type === 'MULTIPLE_CHOICE') {
            const selectedOption = currentQ.config.options?.find(o => o.id === answers[currentQ.id]);
            if (selectedOption?.action === 'SUBMIT') {
                submitSurvey();
                return;
            }
            if (selectedOption?.next_question_index) {
                const target = questions.findIndex(q => q.order_index === selectedOption.next_question_index);
                if (target !== -1) nextIndex = target;
            }
        } else if (currentQ.config.next_question_index) {
            const target = questions.findIndex(q => q.order_index === currentQ.config.next_question_index);
            if (target !== -1) nextIndex = target;
        }

        if (nextIndex >= 0 && nextIndex < questions.length) {
            setCurrentQIndex(nextIndex);
        } else {
            submitSurvey();
        }
    }, 150);
  };

  const prevQuestion = () => {
    if (historyStack.length > 0) {
      const prevIndex = historyStack[historyStack.length - 1];
      setHistoryStack(prev => prev.slice(0, -1));
      setCurrentQIndex(prevIndex);
    } else {
      setViewState('INTRO');
    }
  };

  const submitSurvey = async () => {
    setIsSubmitting(true);
    try {
      const answerArray = Object.entries(answers).map(([qId, val]) => ({ questionId: qId, value: val }));
      const result = await submitKioskResponse(survey.id, accessCode, answerArray);
      if (result && result.wonItem) {
          console.log("Won item received:", result.wonItem);
          setWonItem(result.wonItem);
      }
      setViewState('SUCCESS');
    } catch (e) {
      alert("Submission failed. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenBox = () => {
      setIsUnboxing(true);
      setTimeout(() => { setBoxOpen(true); setIsUnboxing(false); }, 3000); // 3s Animation
  };

  // --- KIOSK UI RENDERERS ---
  
  if (viewState === 'SUCCESS') {
      return (
          <KioskContainer isKioskMode={true} toggleKioskMode={toggleFullscreen}>
              <style dangerouslySetInnerHTML={{__html: `
                @keyframes shake {
                  0% { transform: translate(1px, 1px) rotate(0deg); }
                  10% { transform: translate(-1px, -2px) rotate(-1deg); }
                  20% { transform: translate(-3px, 0px) rotate(1deg); }
                  30% { transform: translate(3px, 2px) rotate(0deg); }
                  40% { transform: translate(1px, -1px) rotate(1deg); }
                  50% { transform: translate(-1px, 2px) rotate(-1deg); }
                  60% { transform: translate(-3px, 1px) rotate(0deg); }
                  70% { transform: translate(3px, 1px) rotate(-1deg); }
                  80% { transform: translate(-1px, -1px) rotate(1deg); }
                  90% { transform: translate(1px, 2px) rotate(0deg); }
                  100% { transform: translate(1px, -2px) rotate(-1deg); }
                }
                .animate-box-shake {
                  animation: shake 0.5s;
                  animation-iteration-count: infinite;
                }
              `}} />
              
              <div className="w-full h-full flex flex-col items-center justify-center bg-slate-900 text-center p-4 md:p-8 relative overflow-hidden">
                  {/* Decor */}
                  <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
                       <div className="absolute top-[-20%] right-[-20%] w-[80%] h-[80%] bg-indigo-500 rounded-full blur-[150px]" />
                  </div>

                  {wonItem ? (
                      <div className="relative z-10 max-w-2xl w-full">
                         {!boxOpen ? (
                             <div className="cursor-default">
                                 <div className={`transition-transform duration-300 ${isUnboxing ? 'animate-box-shake' : ''}`}>
                                     <Box size={240} className="text-yellow-400 mx-auto drop-shadow-[0_0_60px_rgba(250,204,21,0.6)]" strokeWidth={1} />
                                     <p className="mt-8 text-4xl font-black text-white animate-pulse">
                                         {isUnboxing ? 'Unlocking Reward...' : 'Mystery Box Found!'}
                                     </p>
                                 </div>
                             </div>
                         ) : (
                             <div className="bg-white rounded-3xl md:rounded-[3rem] p-6 md:p-12 shadow-2xl border-4 md:border-8 border-indigo-500 animate-in zoom-in duration-500">
                                 <div className="text-center mb-6 md:mb-8 border-b-2 md:border-b-4 border-dashed border-gray-200 pb-4 md:pb-6">
                                     <span className="bg-indigo-600 text-white px-4 py-1 md:px-6 md:py-2 rounded-full text-sm md:text-xl font-bold uppercase tracking-widest shadow-lg">You Won</span>
                                     <h2 className="text-3xl md:text-5xl font-black text-slate-900 mt-4 md:mt-6">{wonItem.name}</h2>
                                 </div>
                                 <div className="bg-slate-50 rounded-2xl md:rounded-3xl p-4 md:p-8 mb-6 md:mb-8 flex items-center justify-center h-48 md:h-64">
                                     {wonItem.image_url ? (
                                         <img src={wonItem.image_url} alt={wonItem.name} className="h-full object-contain" />
                                     ) : (
                                         <Gift size={80} className="md:w-[120px] md:h-[120px] text-indigo-400" />
                                     )}
                                 </div>
                                 <button onClick={handleReset} className="w-full bg-indigo-600 text-white font-black py-4 md:py-6 rounded-xl md:rounded-2xl text-xl md:text-3xl shadow-xl hover:bg-indigo-700 active:scale-95 transition-all flex items-center justify-center gap-2 md:gap-4">
                                     <RefreshCw size={24} className="md:w-[32px] md:h-[32px]"/> Collect & Finish
                                 </button>
                             </div>
                         )}
                      </div>
                  ) : (
                      <div className="relative z-10">
                          <Check size={80} className="md:w-[120px] md:h-[120px] text-emerald-400 mx-auto mb-6 md:mb-8 drop-shadow-[0_0_30px_rgba(16,185,129,0.5)]" />
                          <h1 className="text-4xl md:text-6xl font-black text-white mb-4 md:mb-6">Thank You!</h1>
                          <p className="text-xl md:text-3xl text-slate-400 mb-8 md:mb-16">Your response has been saved.</p>
                          <div className="inline-flex items-center gap-3 bg-slate-800/80 px-6 py-3 md:px-8 md:py-4 rounded-full text-lg md:text-xl text-slate-300 mb-8">
                             <Loader2 className="animate-spin" /> Resetting in {autoResetTimer}s
                          </div>
                          <div>
                              <button onClick={handleReset} className="bg-white text-slate-900 px-8 py-3 md:px-12 md:py-5 rounded-full text-xl md:text-2xl font-bold hover:scale-105 transition-transform">
                                  Start New Survey
                              </button>
                          </div>
                      </div>
                  )}
              </div>
          </KioskContainer>
      );
  }

  if (viewState === 'RUNNER') {
      const currentQ = questions[currentQIndex];
      const answerVal = answers[currentQ.id];
      const hasAnswer = isAnswered(currentQ, answerVal);

      const progress = ((currentQIndex + 1) / questions.length) * 100;

      return (
          <KioskContainer isKioskMode={true} toggleKioskMode={toggleFullscreen}>
              <div className="h-full flex flex-col bg-slate-900 overflow-hidden">
                  {/* Progress Header */}
                  <div className="px-4 py-3 md:px-8 md:py-6 flex items-center justify-between bg-slate-800/50 shrink-0">
                      <button onClick={prevQuestion} className="bg-slate-700 p-2 md:p-4 rounded-full text-white hover:bg-slate-600 transition-colors">
                          <ArrowLeft size={24} className="md:w-8 md:h-8" />
                      </button>
                      <div className="flex-1 mx-4 md:mx-8 h-3 md:h-4 bg-slate-700 rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-500 transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
                      </div>
                      <span className="text-lg md:text-xl font-mono text-slate-400 font-bold">{currentQIndex + 1}/{questions.length}</span>
                  </div>

                  {/* Main Content - Added bottom padding to ensure content isn't covered by footer */}
                  <div className="flex-1 flex flex-col items-center justify-center p-4 md:p-8 overflow-y-auto pb-40">
                      <div className="max-w-5xl w-full text-center mb-6 md:mb-12 animate-in slide-in-from-right duration-300">
                          {/* Priority: Non-English First */}
                          <h2 className="text-2xl md:text-4xl lg:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight">
                              {getPrimaryText(currentQ.question_text)}
                          </h2>
                          {getSecondaryText(currentQ.question_text) && (
                              <p className="text-lg md:text-2xl lg:text-3xl text-indigo-300 font-khmer opacity-90">
                                  {getSecondaryText(currentQ.question_text)}
                              </p>
                          )}
                      </div>

                      <div className="w-full max-w-5xl animate-in fade-in zoom-in duration-300 delay-100">
                          <KioskInput 
                             q={currentQ} 
                             value={answers[currentQ.id]} 
                             onChange={handleAnswerChange} 
                          />
                      </div>
                  </div>

                  {/* Footer - Fixed at bottom of container */}
                  <div className="p-4 md:p-8 bg-slate-800/90 backdrop-blur flex justify-between items-center absolute bottom-0 left-0 right-0 border-t border-slate-700/50 z-20">
                      <button onClick={handleReset} className="text-slate-500 text-sm md:text-lg font-medium px-4 py-2 md:px-8 md:py-4">Cancel</button>
                      <button 
                         onClick={() => nextQuestion()}
                         disabled={(currentQ.config.required !== false && !hasAnswer) || isValidating}
                         className={`px-8 py-3 md:px-16 md:py-6 rounded-xl md:rounded-2xl text-xl md:text-3xl font-bold flex items-center gap-2 md:gap-4 transition-all shadow-xl ${
                             (currentQ.config.required !== false && !hasAnswer)
                             ? 'bg-slate-700 text-slate-500 opacity-50'
                             : 'bg-indigo-600 text-white hover:scale-105 hover:bg-indigo-500'
                         }`}
                      >
                         {isValidating ? <Loader2 className="animate-spin md:w-[32px] md:h-[32px]" size={20} /> : (isSubmitting ? 'Sending...' : 'Next')} 
                         {!isSubmitting && !isValidating && <ArrowRight size={20} className="md:w-[32px] md:h-[32px]" />}
                      </button>
                  </div>
              </div>
          </KioskContainer>
      );
  }

  // INTRO VIEW
  const isActive = survey.status === 'ACTIVE';
  
  return (
    <KioskContainer isKioskMode={true} toggleKioskMode={toggleFullscreen}>
        <div className="h-full flex flex-col items-center justify-center bg-slate-900 text-center p-4 md:p-8 relative overflow-hidden">
             {/* Attract Mode Background */}
             <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-900/40 via-slate-900 to-slate-900 animate-pulse-slow"></div>

             <div className="relative z-10 max-w-6xl w-full">
                 {isActive ? (
                     <>
                        <div className="mx-auto mb-8 md:mb-12 animate-bounce-slow drop-shadow-[0_0_80px_rgba(79,70,229,0.5)]">
                            <Logo size={140} className="md:w-[200px] md:h-[200px] mx-auto" />
                        </div>

                        {/* Priority: Non-English First */}
                        <h1 className="text-5xl md:text-7xl lg:text-9xl font-black text-white mb-4 md:mb-8 tracking-tighter leading-none">
                            {getPrimaryText(survey.title)}
                        </h1>
                        {getSecondaryText(survey.title) && (
                            <h2 className="text-2xl md:text-4xl lg:text-5xl text-indigo-300 font-khmer mb-12 md:mb-20 opacity-90">
                                {getSecondaryText(survey.title)}
                            </h2>
                        )}
                        <button 
                            onClick={startSurvey}
                            className="bg-white text-indigo-950 text-2xl md:text-4xl lg:text-5xl font-black px-10 py-5 md:px-24 md:py-10 rounded-full shadow-[0_20px_60px_rgba(255,255,255,0.2)] hover:scale-105 active:scale-95 transition-all flex items-center gap-4 md:gap-8 mx-auto group"
                        >
                            <span>Touch to Start</span>
                            <ArrowRight size={32} className="md:w-[48px] md:h-[48px] group-hover:translate-x-4 transition-transform"/>
                        </button>
                     </>
                 ) : (
                     <div className="opacity-50">
                         <Ban size={80} className="md:w-[120px] md:h-[120px] mx-auto mb-8 text-slate-500" />
                         <h1 className="text-4xl md:text-6xl font-bold text-white">Currently Unavailable</h1>
                         <p className="text-xl md:text-2xl text-slate-400 mt-4">This survey kiosk is inactive.</p>
                     </div>
                 )}
             </div>

             {/* Mode Toggle Hidden Button */}
             <button onClick={toggleFullscreen} className="absolute top-4 right-4 p-4 opacity-0 hover:opacity-20 text-white">
                 {isFullscreen ? <Minimize size={32}/> : <Maximize size={32}/>}
             </button>

             {/* Brand footer */}
             <div className="absolute bottom-6 md:bottom-10 left-0 right-0 flex items-center justify-center gap-2 text-slate-500 z-10">
                 <Logo size={22} />
                 <span className="text-sm md:text-base font-semibold tracking-wide">Powered by Tinaney</span>
             </div>
        </div>
    </KioskContainer>
  );
};

// --- SUB COMPONENTS FOR KIOSK INPUTS ---
const KioskInput = ({ q, value, onChange }: { q: SurveyQuestionDraft, value: any, onChange: (v: any) => void }) => {
    // Rating
    if (q.question_type === 'RATING') {
        const min = q.config.min || 1;
        const max = q.config.max || 5;
        const range = Array.from({length: (max - min) + 1}, (_, i) => min + i);
        return (
            <div className="flex flex-wrap justify-center gap-3 md:gap-6">
                {range.map(val => (
                    <button
                        key={val}
                        onClick={() => onChange(val)}
                        className={`w-20 h-20 text-3xl md:w-40 md:h-40 md:rounded-3xl rounded-xl md:text-5xl font-bold border-4 transition-all ${
                            value === val 
                            ? 'bg-yellow-400 border-yellow-300 text-black scale-110 shadow-[0_0_40px_rgba(250,204,21,0.5)]' 
                            : 'bg-slate-800 border-slate-600 text-white hover:border-slate-400 hover:bg-slate-700'
                        }`}
                    >
                        {val}
                    </button>
                ))}
            </div>
        );
    }
    // Multiple Choice
    if (q.question_type === 'MULTIPLE_CHOICE') {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full">
                {q.config.options?.map(opt => (
                    <button
                        key={opt.id}
                        onClick={() => onChange(opt.id)}
                        className={`p-5 md:p-10 rounded-xl md:rounded-2xl border-2 md:border-4 text-left transition-all relative overflow-hidden group ${
                            value === opt.id 
                            ? 'bg-indigo-600 border-indigo-400 shadow-2xl scale-[1.02]' 
                            : 'bg-slate-800 border-slate-600 hover:bg-slate-750 hover:border-slate-400'
                        }`}
                    >
                        <div className="flex items-center gap-4 md:gap-6 relative z-10">
                            <div className={`w-8 h-8 md:w-12 md:h-12 rounded-full border-2 md:border-4 flex items-center justify-center shrink-0 ${value === opt.id ? 'bg-white border-white' : 'border-slate-500'}`}>
                                {value === opt.id && <div className="w-4 h-4 md:w-6 md:h-6 rounded-full bg-indigo-600" />}
                            </div>
                            <div>
                                <span className="block text-xl md:text-3xl lg:text-4xl font-bold text-white">{getPrimaryText(opt.label)}</span>
                                {getSecondaryText(opt.label) && <span className="block text-sm md:text-xl text-indigo-200 mt-1 md:mt-2 font-khmer">{getSecondaryText(opt.label)}</span>}
                            </div>
                        </div>
                    </button>
                ))}
            </div>
        );
    }
    // Checkbox
    if (q.question_type === 'CHECKBOX') {
        const selected = (value as string[]) || [];
        const toggle = (id: string) => {
            const newSel = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
            onChange(newSel);
        };
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full">
                {q.config.options?.map(opt => (
                    <button
                        key={opt.id}
                        onClick={() => toggle(opt.id)}
                        className={`p-5 md:p-8 rounded-xl md:rounded-2xl border-2 md:border-4 text-left transition-all flex items-center gap-4 md:gap-6 ${
                            selected.includes(opt.id)
                            ? 'bg-indigo-600 border-indigo-400 shadow-xl' 
                            : 'bg-slate-800 border-slate-600'
                        }`}
                    >
                        <div className={`w-8 h-8 md:w-12 md:h-12 rounded border-2 md:border-4 flex items-center justify-center shrink-0 ${selected.includes(opt.id) ? 'bg-white border-white' : 'border-slate-500'}`}>
                            {selected.includes(opt.id) && <Check size={20} className="md:w-[32px] md:h-[32px] text-indigo-600" strokeWidth={4} />}
                        </div>
                        <span className="text-xl md:text-3xl font-bold text-white">{getPrimaryText(opt.label)}</span>
                    </button>
                ))}
            </div>
        );
    }
    
    // Matrix
    if (q.question_type === 'MATRIX') {
        const kMatrixVal = (value as Record<string, string>) || {};
        const handleKMatrixChange = (rId: string, cId: string) => {
            onChange({ ...kMatrixVal, [rId]: cId });
        };

        return (
            <div className="w-full space-y-6">
                {q.config.rows?.map((row, idx) => (
                    <div key={row.id} className="bg-slate-800/60 p-6 rounded-2xl border border-slate-700 animate-in slide-in-from-bottom-4" style={{ animationDelay: `${idx * 100}ms` }}>
                        <h3 className="text-xl md:text-2xl font-bold text-white mb-4 text-left border-l-4 border-indigo-500 pl-4">
                            {getPrimaryText(row.label)}
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {q.config.columns?.map((col) => {
                                const active = kMatrixVal[row.id] === col.id;
                                return (
                                    <button
                                        key={col.id}
                                        onClick={() => handleKMatrixChange(row.id, col.id)}
                                        className={`py-4 px-2 rounded-xl border-2 font-bold text-lg transition-all active:scale-95 ${
                                            active 
                                            ? 'bg-indigo-600 border-indigo-400 text-white shadow-lg' 
                                            : 'bg-slate-900 border-slate-600 text-slate-400 hover:border-slate-500'
                                        }`}
                                    >
                                        {getPrimaryText(col.label)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // Text
    if (q.question_type === 'TEXT') {
        return (
            <textarea 
                value={value || ''} 
                onChange={e => onChange(e.target.value)} 
                placeholder="Type your answer here..." 
                className="w-full h-64 bg-slate-800 border-4 border-slate-600 rounded-3xl p-8 text-white text-3xl focus:ring-4 focus:ring-indigo-500 outline-none resize-none shadow-inner" 
            />
        );
    }

    // Date
    if (q.question_type === 'DATE') {
        return (
            <input 
                type="date" 
                value={value || ''} 
                onChange={e => onChange(e.target.value)} 
                className="w-full bg-slate-800 border-4 border-slate-600 rounded-3xl py-8 px-8 text-white text-4xl focus:ring-4 focus:ring-indigo-500 outline-none [color-scheme:dark] text-center h-32" 
            />
        );
    }

    // Geo Location
    if (q.question_type === 'GEO_LOCATION') {
        return (
            <div className="flex flex-col items-center gap-8 py-12">
                 {value ? (
                     <div className="bg-emerald-500/10 border-4 border-emerald-500/50 text-emerald-400 p-12 rounded-3xl flex flex-col items-center gap-6 w-full max-w-2xl">
                         <MapPin size={64} />
                         <p className="font-mono text-3xl">{value.lat.toFixed(5)}, {value.lng.toFixed(5)}</p>
                         <button onClick={() => onChange(null)} className="text-xl underline p-4">Clear Location</button>
                     </div>
                 ) : (
                     <button 
                        onClick={() => navigator.geolocation.getCurrentPosition(p => onChange({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }))} 
                        className="bg-indigo-600 text-white px-12 py-8 rounded-3xl flex items-center gap-4 font-bold text-3xl shadow-xl active:scale-95 transition-transform"
                     >
                        <Locate size={48}/> Share Current Location
                     </button>
                 )}
            </div>
        );
    }

    // Image Upload
    if (q.question_type === 'IMAGE_UPLOAD') {
        const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files[0]) {
                try {
                    const url = await uploadSurveyFile(e.target.files[0]);
                    onChange(url);
                } catch (err) {
                    alert("Upload failed");
                }
            }
        };
        return (
            <div className="flex flex-col items-center gap-8 py-8 w-full">
                {value ? (
                    <div className="relative group w-full max-w-2xl">
                        <img src={value} alt="Uploaded" className="w-full rounded-3xl border-4 border-slate-700 shadow-2xl" />
                        <button onClick={() => onChange(null)} className="absolute top-4 right-4 bg-red-600 text-white p-4 rounded-full shadow-xl hover:bg-red-500 transition-colors">
                            <Trash2 size={32} />
                        </button>
                    </div>
                ) : (
                    <label className="w-full max-w-3xl cursor-pointer bg-slate-800 hover:bg-slate-750 border-4 border-dashed border-slate-600 hover:border-indigo-500 rounded-[3rem] p-20 flex flex-col items-center gap-8 transition-all group active:scale-95">
                        <div className="w-32 h-32 bg-slate-700 group-hover:bg-indigo-500/20 rounded-full flex items-center justify-center text-slate-400 group-hover:text-indigo-400 transition-colors">
                            <UploadCloud size={64} />
                        </div>
                        <span className="text-slate-400 group-hover:text-white font-bold text-3xl">Tap to Take Photo / Upload</span>
                        <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                    </label>
                )}
            </div>
        );
    }

    // Ranking
    if (q.question_type === 'RANKING') {
        const items = (value as string[]) || q.config.options?.map(o => o.id) || [];
        
        const moveItem = (index: number, direction: 'up' | 'down') => {
            const newItems = [...items];
            if (direction === 'up' && index > 0) {
                [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
            } else if (direction === 'down' && index < newItems.length - 1) {
                [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
            }
            onChange(newItems);
        };

        return (
            <div className="max-w-3xl mx-auto space-y-4 w-full">
                {items.map((itemId, index) => {
                    const option = q.config.options?.find(o => o.id === itemId);
                    if (!option) return null;
                    return (
                        <div key={itemId} className="bg-slate-800 p-6 rounded-2xl border-2 border-slate-700 flex items-center justify-between gap-6 animate-in slide-in-from-bottom-2">
                            <div className="flex items-center gap-6">
                                <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center font-bold text-slate-400 text-xl">
                                    {index + 1}
                                </div>
                                <span className="font-bold text-white text-2xl">{getPrimaryText(option.label)}</span>
                            </div>
                            <div className="flex flex-col gap-2">
                                <button 
                                    onClick={() => moveItem(index, 'up')} 
                                    disabled={index === 0}
                                    className="p-3 bg-slate-700 hover:bg-slate-600 rounded-xl text-white disabled:opacity-30 active:scale-95"
                                >
                                    <ArrowUp size={32} />
                                </button>
                                <button 
                                    onClick={() => moveItem(index, 'down')} 
                                    disabled={index === items.length - 1}
                                    className="p-3 bg-slate-700 hover:bg-slate-600 rounded-xl text-white disabled:opacity-30 active:scale-95"
                                >
                                    <ArrowDown size={32} />
                                </button>
                            </div>
                        </div>
                    );
                })}
                {!value && (
                    <div className="text-center mt-8">
                        <button 
                            onClick={() => onChange(q.config.options?.map(o => o.id))} 
                            className="text-indigo-400 text-xl font-bold underline p-4"
                        >
                            Tap to Start Ranking
                        </button>
                    </div>
                )}
            </div>
        );
    }
    
    // Default Fallback
    return <div className="text-lg md:text-2xl text-slate-400">This question type is not optimized for Kiosk mode.</div>;
};

export default SurveyResponseKioskView;
